import { supabase } from '@/lib/supabase';

export type HabitStatus = 'confirmed_early' | 'on_time' | 'late' | 'missed' | 'shabbat' | 'holiday' | 'pending';

/** A single habit's status within a day. */
export interface HabitEntry {
  habitTypeId: string;
  name: string;
  icon: string | null;
  status: HabitStatus;
  time: string | null;
  delta: number | null;
}

/** Per-day summary shaped for the dashboard/calendar (morning + bedtime slots). */
export interface DailyRecord {
  date: string;
  morningStatus: HabitStatus;
  morningTime: string | null;
  morningDelta: number | null;
  bedtimeStatus: HabitStatus;
  bedtimeTime: string | null;
  bedtimeDelta: number | null;
  /** Every habit recorded for this day (in the order returned by the DB). */
  habits: HabitEntry[];
}

/** Raw daily_records row joined with its habit type. */
export interface RawDailyRecord {
  id: string;
  user_id: string;
  habit_type_id: string;
  record_date: string;
  scheduled_time: string | null;
  actual_confirmation_time: string | null;
  delta_minutes: number | null;
  status: HabitStatus;
  notes: string | null;
  habit_types: { name: string; calendar_icon: string | null } | null;
}

function formatClock(iso: string | null, timezone?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(d);
}

function isMorningHabit(name: string | undefined): boolean {
  return /morning/i.test(name || '');
}

function isBedtimeHabit(name: string | undefined): boolean {
  return /bedtime|night/i.test(name || '');
}

/** Fetch raw records for a date range (inclusive, YYYY-MM-DD strings). */
export async function fetchRawRecords(startDate: string, endDate: string): Promise<RawDailyRecord[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return [];

  const { data, error } = await supabase
    .from('daily_records')
    .select('id, user_id, habit_type_id, record_date, scheduled_time, actual_confirmation_time, delta_minutes, status, notes, habit_types(name, calendar_icon)')
    .eq('user_id', session.user.id)
    .gte('record_date', startDate)
    .lte('record_date', endDate)
    .order('record_date', { ascending: true });

  if (error) {
    console.error('Failed to load daily records:', error);
    return [];
  }
  return (data || []) as unknown as RawDailyRecord[];
}

/**
 * Fetch records mapped into the morning/bedtime day-summary shape
 * used by the dashboard and calendar views.
 */
export async function fetchDayRecords(
  startDate: string,
  endDate: string,
  timezone?: string,
): Promise<Record<string, DailyRecord>> {
  const raw = await fetchRawRecords(startDate, endDate);
  const byDate: Record<string, DailyRecord> = {};

  for (const r of raw) {
    const entry = byDate[r.record_date] || {
      date: r.record_date,
      morningStatus: 'pending' as HabitStatus,
      morningTime: null,
      morningDelta: null,
      bedtimeStatus: 'pending' as HabitStatus,
      bedtimeTime: null,
      bedtimeDelta: null,
      habits: [] as HabitEntry[],
    };

    const name = r.habit_types?.name;
    const time = formatClock(r.actual_confirmation_time, timezone);

    // Always record every habit so the calendar can show all of them.
    entry.habits.push({
      habitTypeId: r.habit_type_id,
      name: name || 'Habit',
      icon: r.habit_types?.calendar_icon ?? null,
      status: r.status,
      time,
      delta: r.delta_minutes,
    });

    // Keep the morning/bedtime summary slots for the dashboard widgets.
    if (isMorningHabit(name)) {
      entry.morningStatus = r.status;
      entry.morningTime = time;
      entry.morningDelta = r.delta_minutes;
    } else if (isBedtimeHabit(name)) {
      entry.bedtimeStatus = r.status;
      entry.bedtimeTime = time;
      entry.bedtimeDelta = r.delta_minutes;
    }

    byDate[r.record_date] = entry;
  }

  return byDate;
}
